import type { ReactNode } from 'react';
import { cn } from '../cn.js';
import { Logo } from './logo.js';

/**
 * Coquille applicative commune aux deux fronts : barre latérale fixe à gauche,
 * contenu défilable à droite. Le rendu est identique dans le backoffice et le
 * dashboard client, seuls les items de navigation et le sous-titre du logo
 * changent (cf. maquettes).
 */

export interface NavEntry {
  readonly label: string;
  readonly to: string;
  readonly badge?: number;
}

export function AppShell({
  logoSubtitle,
  nav,
  currentPath,
  onNavigate,
  footer,
  children,
}: {
  logoSubtitle?: string;
  nav: readonly NavEntry[];
  currentPath: string;
  onNavigate: (to: string) => void;
  footer?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex min-h-screen bg-void">
      <aside className="flex w-60 shrink-0 flex-col border-r border-border-subtle bg-surface-sunken">
        <div className="px-5 py-5">
          <Logo subtitle={logoSubtitle} />
        </div>

        <nav className="flex flex-1 flex-col gap-0.5 px-2" aria-label="Navigation principale">
          {nav.map((entry) => {
            // Correspondance par préfixe : `/alertes/:id` doit garder l'entrée
            // « Alertes » active. La racine `/` est traitée à part sinon elle
            // matcherait toutes les routes.
            const active =
              entry.to === '/' ? currentPath === '/' : currentPath.startsWith(entry.to);

            return (
              <button
                key={entry.to}
                type="button"
                onClick={() => onNavigate(entry.to)}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'flex items-center justify-between rounded-lg px-3 py-2.5 text-sm transition-colors',
                  active
                    ? 'border-l-2 border-brand-500 bg-brand-500/8 font-semibold text-brand-400'
                    : 'border-l-2 border-transparent text-text-muted hover:bg-surface-raised hover:text-text',
                )}
              >
                {entry.label}
                {entry.badge ? (
                  <span className="rounded bg-critical/15 px-1.5 py-0.5 font-mono text-[0.625rem] font-bold text-critical">
                    {entry.badge}
                  </span>
                ) : null}
              </button>
            );
          })}
        </nav>

        {footer ? (
          <div className="border-t border-border-subtle px-5 py-4 text-xs">{footer}</div>
        ) : null}
      </aside>

      <main className="min-w-0 flex-1 overflow-x-hidden px-8 py-7">{children}</main>
    </div>
  );
}

export interface BreadcrumbEntry {
  readonly label: string;
  /** Absent sur le dernier segment, qui désigne la page courante. */
  readonly onClick?: () => void;
}

export function PageHeader({
  title,
  subtitle,
  action,
  back,
  breadcrumb,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  back?: { label: string; onClick: () => void };
  /**
   * Chemin hiérarchique (Clients / Mairie de Chambéry / C001-…). Préférable au
   * simple « Retour » dès qu'une page a plusieurs parents possibles : le fil
   * d'Ariane dit où l'on est, le retour ne dit que d'où l'on vient.
   */
  breadcrumb?: readonly BreadcrumbEntry[];
}) {
  return (
    <header className="mb-6">
      {breadcrumb && breadcrumb.length > 0 ? (
        <nav aria-label="Fil d’Ariane" className="mb-3 flex flex-wrap items-center gap-1.5 text-sm">
          {breadcrumb.map((entry, index) => (
            <span key={`${entry.label}-${index}`} className="flex items-center gap-1.5">
              {index > 0 ? (
                <span className="text-text-dim" aria-hidden="true">
                  /
                </span>
              ) : null}
              {entry.onClick ? (
                <button
                  type="button"
                  onClick={entry.onClick}
                  className="text-text-muted transition-colors hover:text-brand-400"
                >
                  {entry.label}
                </button>
              ) : (
                <span className="font-medium text-text" aria-current="page">
                  {entry.label}
                </span>
              )}
            </span>
          ))}
        </nav>
      ) : null}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold tracking-tight text-text">{title}</h1>
          {subtitle ? <p className="mt-1 text-sm text-text-muted">{subtitle}</p> : null}
        </div>
        {action}
      </div>
      {back ? (
        <button
          type="button"
          onClick={back.onClick}
          className="mt-4 text-sm text-brand-400 transition-colors hover:text-brand-300"
        >
          ← {back.label}
        </button>
      ) : null}
    </header>
  );
}

export interface TabEntry<T extends string> {
  readonly value: T;
  readonly label: string;
  /** Compteur affiché à droite du libellé (nœuds, alertes ouvertes…). */
  readonly count?: number;
}

/**
 * Onglets de navigation d'une fiche (sections d'un même objet), à distinguer de
 * `FilterTabs` qui restreint un même tableau. Le composant ne connaît pas le
 * routeur : la page traduit la valeur active en URL, ce qui garde chaque onglet
 * adressable par lien direct.
 */
export function TabBar<T extends string>({
  tabs,
  value,
  onChange,
}: {
  tabs: readonly TabEntry<T>[];
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div
      className="mb-6 flex flex-wrap gap-1 border-b border-border-subtle"
      role="tablist"
      aria-label="Sections"
    >
      {tabs.map((tab) => {
        const active = tab.value === value;
        return (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(tab.value)}
            className={cn(
              '-mb-px flex items-center gap-2 border-b-2 px-3.5 py-2.5 text-sm transition-colors',
              active
                ? 'border-brand-500 font-semibold text-text'
                : 'border-transparent text-text-muted hover:border-border-strong hover:text-text',
            )}
          >
            {tab.label}
            {tab.count !== undefined ? (
              <span
                className={cn(
                  'rounded px-1.5 py-0.5 font-mono text-[0.625rem] font-bold',
                  active ? 'bg-brand-500/15 text-brand-400' : 'bg-surface-raised text-text-dim',
                )}
              >
                {tab.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

/** Groupe de filtres en pastilles (onglets « Toutes / Critiques / … »). */
export function FilterTabs<T extends string>({
  options,
  value,
  onChange,
}: {
  options: readonly { readonly value: T; readonly label: string }[];
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2" role="tablist">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="tab"
          aria-selected={option.value === value}
          onClick={() => onChange(option.value)}
          className={cn(
            'rounded-lg px-3.5 py-2 text-sm transition-colors',
            option.value === value
              ? 'bg-brand-500/10 font-semibold text-brand-400 ring-1 ring-brand-500/30'
              : 'text-text-muted ring-1 ring-border-subtle hover:bg-surface-raised hover:text-text',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
